/**
 * useDragAndDrop Hook
 *
 * Reusable native HTML5 drag-and-drop functionality for Campaign Navigation Panel.
 * Supports folders, labels, tags, and campaigns with desktop-only detection.
 *
 * Features:
 * - Native HTML5 drag events (no external libraries)
 * - Visual feedback (opacity, border highlight, cursors)
 * - Drop zone indicators (before, after, inside)
 * - Validation (prevent invalid drops, max depth, self-drop)
 * - Desktop-only: Checks pointer type and viewport width
 * - TypeScript type safety
 *
 * @example
 * ```tsx
 * const { dragProps, dropProps, isDragging, isOver, canDrop } = useDragAndDrop({
 *   onDrop: (item, targetId, position) => {
 *     console.log(`Drop ${item.id} ${position} ${targetId}`);
 *   },
 *   acceptTypes: ['folder', 'campaign'],
 *   maxDepth: 2,
 * });
 *
 * return (
 *   <div {...dragProps} {...dropProps} className={isDragging ? 'opacity-50' : ''}>
 *     Content
 *   </div>
 * );
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Drag item types
 */
export type DragItemType = 'folder' | 'label' | 'tag' | 'campaign';

/**
 * Drag item data structure
 */
export interface DragItem {
  /** Type of item being dragged */
  type: DragItemType;
  /** Unique ID of the item */
  id: string;
  /** For campaigns: source folder ID (null if from root) */
  sourceId?: string;
  /** Current nesting level (for folders) */
  level?: number;
  /** Parent ID (for folders) */
  parentId?: string | null;
}

/**
 * Drop position relative to target
 */
export type DropPosition = 'before' | 'after' | 'inside';

/**
 * Hook options
 */
export interface UseDragAndDropOptions {
  /** Item data to be dragged */
  item: DragItem;
  /** Callback when item is dropped on this target */
  onDrop: (draggedItem: DragItem, targetId: string, position: DropPosition) => void;
  /** Types of items this target can accept */
  acceptTypes: DragItemType[];
  /** Maximum nesting depth (for folders, default: 2) */
  maxDepth?: number;
  /** Allow drop inside this target (for folders/containers) */
  allowDropInside?: boolean;
  /** Custom validation function */
  canAcceptDrop?: (draggedItem: DragItem, position: DropPosition) => boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Hook return value
 */
export interface UseDragAndDropReturn {
  /** Props to spread on draggable element */
  dragProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
    style?: React.CSSProperties;
  };
  /** Props to spread on drop target element */
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    'data-drop-position'?: DropPosition;
  };
  /** Is this item currently being dragged */
  isDragging: boolean;
  /** Is another item being dragged over this target */
  isOver: boolean;
  /** Can the current dragged item be dropped here */
  canDrop: boolean;
  /** Current drop position (before, after, inside) */
  dropPosition: DropPosition | null;
}

/**
 * Check if device is desktop (fine pointer and width >= 768px)
 */
const isDesktop = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check pointer type (fine = mouse/trackpad, coarse = touch)
  const hasFinePointer = window.matchMedia('(pointer: fine)').matches;

  // Check viewport width (>= 768px = tablet/desktop)
  const isWideViewport = window.innerWidth >= 768;

  return hasFinePointer && isWideViewport;
};

/**
 * Get drop position based on mouse position within target
 */
const getDropPosition = (
  e: React.DragEvent,
  allowDropInside: boolean
): DropPosition => {
  const target = e.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const mouseY = e.clientY - rect.top;
  const height = rect.height;

  if (allowDropInside) {
    // Divide into 3 zones: before (top 25%), inside (middle 50%), after (bottom 25%)
    if (mouseY < height * 0.25) return 'before';
    if (mouseY > height * 0.75) return 'after';
    return 'inside';
  } else {
    // Divide into 2 zones: before (top 50%), after (bottom 50%)
    return mouseY < height * 0.5 ? 'before' : 'after';
  }
};

/**
 * useDragAndDrop Hook
 *
 * Provides native HTML5 drag-and-drop functionality with desktop detection.
 */
export const useDragAndDrop = (options: UseDragAndDropOptions): UseDragAndDropReturn => {
  const {
    item,
    onDrop,
    acceptTypes,
    maxDepth = 2,
    allowDropInside = false,
    canAcceptDrop,
    disabled = false,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const [canDrop, setCanDrop] = useState(false);
  const [dropPosition, setDropPosition] = useState<DropPosition | null>(null);
  const [isDesktopDevice, setIsDesktopDevice] = useState(true);

  const draggedItemRef = useRef<DragItem | null>(null);
  const dragEnterCounterRef = useRef(0);

  // Check if desktop on mount and window resize
  useEffect(() => {
    const checkDevice = () => {
      setIsDesktopDevice(isDesktop());
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !isDesktopDevice) {
        e.preventDefault();
        return;
      }

      // Set drag data
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/json', JSON.stringify(item));

      // Store in ref for validation
      draggedItemRef.current = item;

      setIsDragging(true);

      // Optional: Set custom drag image
      // const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
      // e.dataTransfer.setDragImage(dragImage, 0, 0);
    },
    [item, disabled, isDesktopDevice]
  );

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    draggedItemRef.current = null;
  }, []);

  /**
   * Validate if drop is allowed
   */
  const validateDrop = useCallback(
    (draggedItem: DragItem, position: DropPosition): boolean => {
      // Check if type is accepted
      if (!acceptTypes.includes(draggedItem.type)) {
        return false;
      }

      // Prevent self-drop
      if (draggedItem.id === item.id) {
        return false;
      }

      // Prevent dropping parent folder into its child
      if (draggedItem.type === 'folder' && item.type === 'folder') {
        // Check if target is a descendant of dragged item
        // TODO: Implement full tree traversal if needed
        if (item.parentId === draggedItem.id) {
          return false;
        }
      }

      // Check max depth for folders
      if (draggedItem.type === 'folder' && position === 'inside') {
        const newLevel = (item.level || 0) + 1;
        if (newLevel >= maxDepth) {
          return false;
        }
      }

      // Custom validation
      if (canAcceptDrop && !canAcceptDrop(draggedItem, position)) {
        return false;
      }

      return true;
    },
    [acceptTypes, item, maxDepth, canAcceptDrop]
  );

  /**
   * Handle drag over (required to allow drop)
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !isDesktopDevice) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Get dragged item from ref
      const draggedItem = draggedItemRef.current;
      if (!draggedItem) return;

      // Calculate drop position
      const position = getDropPosition(e, allowDropInside);
      setDropPosition(position);

      // Validate drop
      const isValid = validateDrop(draggedItem, position);
      setCanDrop(isValid);

      // Set drop effect
      e.dataTransfer.dropEffect = isValid ? 'move' : 'none';
    },
    [disabled, allowDropInside, validateDrop, isDesktopDevice]
  );

  /**
   * Handle drag enter
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !isDesktopDevice) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Increment counter to handle nested elements
      dragEnterCounterRef.current += 1;

      if (dragEnterCounterRef.current === 1) {
        setIsOver(true);
      }
    },
    [disabled, isDesktopDevice]
  );

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !isDesktopDevice) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Decrement counter
      dragEnterCounterRef.current -= 1;

      if (dragEnterCounterRef.current === 0) {
        setIsOver(false);
        setCanDrop(false);
        setDropPosition(null);
      }
    },
    [disabled, isDesktopDevice]
  );

  /**
   * Handle drop
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !isDesktopDevice) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Reset drag enter counter
      dragEnterCounterRef.current = 0;

      // Get dragged item data
      const data = e.dataTransfer.getData('application/json');
      if (!data) {
        setIsOver(false);
        setCanDrop(false);
        setDropPosition(null);
        return;
      }

      const draggedItem: DragItem = JSON.parse(data);
      const position = dropPosition || getDropPosition(e, allowDropInside);

      // Validate and execute drop
      if (validateDrop(draggedItem, position)) {
        onDrop(draggedItem, item.id, position);
      }

      // Reset state
      setIsOver(false);
      setCanDrop(false);
      setDropPosition(null);
    },
    [disabled, item.id, dropPosition, allowDropInside, validateDrop, onDrop, isDesktopDevice]
  );

  // Return props and state
  return {
    dragProps: {
      draggable: !disabled && isDesktopDevice,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      style: isDragging ? { opacity: 0.5, cursor: 'grabbing' } : { cursor: isDesktopDevice ? 'grab' : 'default' },
    },
    dropProps: {
      onDragOver: handleDragOver,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      'data-drop-position': dropPosition || undefined,
    },
    isDragging,
    isOver,
    canDrop,
    dropPosition,
  };
};
